import { useCallback, useEffect, useRef } from 'react'
import { Tune } from '@mui/icons-material'

import { useAppDispatch, useAppSelector } from 'app/hooks'
import { denormalizedFilterProfilesSelectors } from 'app/entities/store/filterProfilesSelectors'
import { setFilterProfileListSync } from 'app/entities/store/filterProfilesSlice'
import {
  useGetSystemScopeFilterProfilesQuery,
  useLazyGetSystemScopeFilterProfileQuery
} from 'app/services/api/filterprofiles'
import { openSystemScopeModal } from 'app/entities/store/modalsSlice'
import { getDefaultProfileId } from 'common/utils/filter/systemScope'

import {
  setStagingProfileFulfilledSync,
  setStagingProfilePendingSync,
  setStagingProfileRejectedSync,
  setSystemScopeIdSync
} from './globalFilterSlice'
import {
  selectSystemScopeMeta,
  selectSystemScopeIdContainer
} from './globalFilterSelectors'
import GlobalFilterQueryStringHandler from './GlobalFilterQueryStringHandler'

const Filters = () => {
  const dispatch = useAppDispatch()
  const systemScopeMeta = useAppSelector(selectSystemScopeMeta)
  const systemScopeContainer = useAppSelector(selectSystemScopeIdContainer)
  const denormalizedFilterProfiles = useAppSelector(
    denormalizedFilterProfilesSelectors.selectAll
  )

  const isProfileListInitalized = useRef(false)
  const isInitialProfileSet = useRef(false)

  const { currentData: filterProfiles } = useGetSystemScopeFilterProfilesQuery()
  const [getSystemScopeFilterProfile] =
    useLazyGetSystemScopeFilterProfileQuery()

  const openFilterModal = useCallback(() => {
    dispatch(openSystemScopeModal())
  }, [dispatch])

  /**
   * Initialize (lite) profile list
   */
  useEffect(() => {
    if (filterProfiles?.length && !isProfileListInitalized.current) {
      dispatch(setFilterProfileListSync(filterProfiles ?? []))
      isProfileListInitalized.current = true
    }
  }, [dispatch, filterProfiles])

  /**
   * Set current selected system scope (init)
   */
  useEffect(() => {
    if (denormalizedFilterProfiles.length && !isInitialProfileSet.current) {
      if (systemScopeContainer.id) {
        // REVIEW: this is redundent unless it want to force change detection?
        // dispatch(setSystemScopeIdSync(systemScopeContainer.id))
      } else {
        const defaultProfileId = getDefaultProfileId(denormalizedFilterProfiles)

        if (defaultProfileId) {
          dispatch(setSystemScopeIdSync(defaultProfileId))
        } else {
          console.warn('Default system scope not found')
        }
      }

      isInitialProfileSet.current = true
    }
  }, [dispatch, denormalizedFilterProfiles, systemScopeContainer.id])

  /**
   * Initial full profile load for system scope line count display?
   */
  useEffect(() => {
    if (systemScopeContainer.id) {
      // REVIEW: other places with fullfilled/rejected dispatch missing this
      dispatch(setStagingProfilePendingSync())

      getSystemScopeFilterProfile(
        String(systemScopeContainer.id),
        true // preferCacheValue
      )
        .unwrap()
        .then((profile) => {
          dispatch(setStagingProfileFulfilledSync(profile))
        })
        .catch((error) => {
          dispatch(setStagingProfileRejectedSync(error))
        })
    }
  }, [dispatch, getSystemScopeFilterProfile, systemScopeContainer.id])

  const isDefaultProfile =
    systemScopeContainer.id?.toString() ===
    systemScopeMeta.defaultProfileId?.toString()

  return (
    <div className='my-1 ml-auto flex lg:flex-row'>
      <GlobalFilterQueryStringHandler />
      <FilterButton onClick={openFilterModal} />
      {!isDefaultProfile && (
        <div className='ml-2 px-2 pt-1 bg-lighthouseBlue text-white rounded font-medium'>
          {systemScopeMeta.numberOfSelected}
        </div>
      )}
    </div>
  )
}

interface IFilterButton {
  onClick: () => void
}

const FilterButton = ({ onClick }: IFilterButton) => {
  return (
    <button
      id='system-scope-filter-toggle-button'
      className='flex items-center mb-1 px-3 py-1 lg:mb-0 bg-gray-500 bg-opacity-10 text-lighthouseBlue rounded'
      onClick={onClick}
    >
      <Tune fontSize='small' />
      <div className='ml-1 font-medium'>System Scope</div>
    </button>
  )
}

export default Filters
